import api from './api';

export interface OCIRegistryConfig {
  id: string;
  name: string;
  url: string;
  authType: 'anonymous' | 'basic' | 'token';
  username?: string;
  password?: string; // Will be '***' from API
  token?: string; // Will be '***' from API
  createdAt: string;
  updatedAt?: string;
}

export interface OCIManifestLayer {
  mediaType: string;
  digest: string;
  size: number;
}

export interface OCIManifest {
  schemaVersion: number;
  mediaType: string;
  config: OCIManifestLayer;
  layers: OCIManifestLayer[];
}

export interface OCIAssetLink {
  id: string;
  registryId: string;
  registryName: string;
  repository: string;
  tag: string;
  digest?: string;
  platform: string;
  s3Key: string;
  lastSynced?: string;
  createdAt: string;
}

export const ociRegistryApi = {
  // Registry CRUD
  listRegistries: async (): Promise<OCIRegistryConfig[]> => {
    const { data } = await api.get<OCIRegistryConfig[]>('/oci-registries');
    return data;
  },

  addRegistry: async (config: {
    name: string;
    url: string;
    authType: string;
    username?: string;
    password?: string;
    token?: string;
  }): Promise<OCIRegistryConfig> => {
    const { data } = await api.post<{ success: boolean; data: OCIRegistryConfig }>('/oci-registries', config);
    return data.data;
  },

  updateRegistry: async (id: string, updates: Partial<OCIRegistryConfig>): Promise<OCIRegistryConfig> => {
    const { data } = await api.put<{ success: boolean; data: OCIRegistryConfig }>(`/oci-registries/${id}`, updates);
    return data.data;
  },

  deleteRegistry: async (id: string): Promise<void> => {
    await api.delete(`/oci-registries/${id}`);
  },

  testConnection: async (id: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post<{ success: boolean; message: string }>(`/oci-registries/${id}/test`);
    return data;
  },

  // Browsing
  listRepositories: async (id: string): Promise<string[]> => {
    const { data } = await api.get<{ repositories: string[] }>(`/oci-registries/${id}/repositories`);
    return data.repositories;
  },

  listTags: async (id: string, repository: string): Promise<string[]> => {
    const { data } = await api.get<{ repository: string; tags: string[] }>(
      `/oci-registries/${id}/tags`,
      { params: { repository } },
    );
    return data.tags;
  },

  getManifest: async (id: string, repository: string, reference: string): Promise<OCIManifest> => {
    const { data } = await api.get<OCIManifest>(
      `/oci-registries/${id}/manifest`,
      { params: { repository, reference } },
    );
    return data;
  },

  // Pull operations
  pullToS3: async (id: string, repository: string, tag: string, s3Key: string, platform: string = 'linux/amd64'): Promise<{ jobId: string }> => {
    const { data } = await api.post<{ success: boolean; jobId: string }>(`/oci-registries/${id}/pull`, {
      repository,
      tag,
      s3Key,
      platform,
    });
    return { jobId: data.jobId };
  },

  // Asset Links
  listLinks: async (): Promise<OCIAssetLink[]> => {
    const { data } = await api.get<OCIAssetLink[]>('/oci-registries/links');
    return data;
  },

  getLinksByS3Key: async (key: string): Promise<OCIAssetLink[]> => {
    const { data } = await api.get<Record<string, OCIAssetLink[]>>('/oci-registries/links/by-s3-key', { params: { key } });
    return data[key] || [];
  },

  getLinksByS3Keys: async (keys: string[]): Promise<Record<string, OCIAssetLink[]>> => {
    const { data } = await api.get<Record<string, OCIAssetLink[]>>('/oci-registries/links/by-s3-key', {
      params: { key: keys.join(',') },
    });
    return data;
  },

  getLinksByImage: async (registryId: string, repository?: string, tag?: string): Promise<OCIAssetLink[]> => {
    const { data } = await api.get<OCIAssetLink[]>('/oci-registries/links/by-image', {
      params: { registryId, repository, tag },
    });
    return data;
  },

  createLink: async (link: {
    registryId: string;
    registryName: string;
    repository: string;
    tag: string;
    platform?: string;
    s3Key: string;
    digest?: string;
  }): Promise<OCIAssetLink> => {
    const { data } = await api.post<{ success: boolean; data: OCIAssetLink }>('/oci-registries/links', link);
    return data.data;
  },

  removeLink: async (id: string): Promise<void> => {
    await api.delete(`/oci-registries/links/${id}`);
  },

  syncLink: async (id: string): Promise<{ jobId: string }> => {
    const { data } = await api.post<{ success: boolean; jobId: string }>(`/oci-registries/links/${id}/sync`);
    return { jobId: data.jobId };
  },
};
